<template>
  <div>
    <AlertBanner v-if="errors.submit" :status="0" class="mb-16">
      {{ errors.submit }}
    </AlertBanner>
    <JobPostConfirmTransitionText v-if="!draft.isPrepopulatedFromReservation" :isAiGenerated="draft.aiGenerated" />
    <h2 v-if="headerText.length" class="text-center mt-32 mb-16 text-h2">{{ headerText }}</h2>
    <p v-if="draft.isPrepopulatedFromReservation" class="mb-28 md:mb-16 text-center">
      Review the information below to make sure it's accurate. This will be shared with caregivers who can help fulfill your
      request.
    </p>
    <p v-else class="mb-24 text-center text-body">
      A title and description have been generated for you. You can edit this at any time.
    </p>

    <form @submit.prevent="submit">
      <Card class="flex flex-col sm:p-24">
        <RouteStepperReview :reviewSections="reviewSections" />
        <p class="text-xs leading-5 text-grey-text-subdued mb-16 border-t-1 border-solid border-grey-divider-subdued pt-24">
          OpenAI used the information you have provided about this job to generate your job description. The generated output
          contain errors. You agree to review it before publishing and understand you are responsible for the information you
          post.
          <a
            class="underline"
            href="https://support.sittercity.com/hc/en-us/articles/23699389621019-Write-your-job-description-with-OpenAI"
            target="_blank"
          >
            Learn more
          </a>
        </p>
        <Button :loading="isLoading" type="submit" size="medium" class="ml-auto w-full md:max-w-max">{{ buttonText }}</Button>
      </Card>
    </form>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import Button from '@/components/button/Button.vue';
import RouteStepperReview from '@/components/route_stepper/RouteStepperReview.vue';
import { ReviewSection } from '@/components/route_stepper/types';
import Card from '@/components/card/Card.vue';
import JobPostConfirmTransitionText from './JobPostConfirmTransitionText.vue';
import { JobPostDraft } from '../types';
import { redirect } from '@/helpers/redirects';
import { sendSegmentEventWithTimeout } from '@/lib/segment';
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';

interface JobPostConfirmData {
  isLoading: boolean;
  errors: {
    submit?: string;
  };
}

export default {
  name: 'JobPostConfirm',
  components: { Button, RouteStepperReview, Card, JobPostConfirmTransitionText, AlertBanner },
  props: {
    stepsData: {
      type: Array as PropType<ReviewSection[]>,
      required: true,
    },
    draft: {
      type: Object as PropType<JobPostDraft>,
      required: true,
    },
  },
  data(): JobPostConfirmData {
    return {
      isLoading: false,
      errors: {},
    };
  },
  computed: {
    redirectPath(): string | null {
      return this.draft.URLSearchParams?.get('redirectPath') || null;
    },
    legacyShareJobRedirect(): string | null {
      // this needed to be carried over from old job post flow.
      // should be refactored to use the general redirectPath instead.
      const legacyShareJobRedirectQueryParam = this.draft.URLSearchParams?.get('jobPostReturnPath');
      if (legacyShareJobRedirectQueryParam) return legacyShareJobRedirectQueryParam;
      return null;
    },
    headlineReviewSection(): ReviewSection[] {
      const headline = this.draft.headlineReviewSection;
      return headline ? [headline] : [];
    },
    reviewSections(): ReviewSection[] {
      const sections = this.stepsData.filter((reviewSection) => reviewSection.title && reviewSection.editPath);
      return [...this.headlineReviewSection, ...sections];
    },
    headerText(): string {
      if (this.draft.isPrepopulatedFromReservation) {
        return "Let's help you find a caregiver by sharing your Back-Up Care request";
      }
      return 'Review your needs';
    },

    buttonText(): string {
      return this.draft.isPrepopulatedFromReservation ? 'Looks good, share with caregivers' : 'Share with sitters';
    },
  },
  methods: {
    async sendAnalyticsEvents(): Promise<void> {
      await this.sendCareTypeSelectionEvent();
      await this.sendJobPostedEvent();
    },

    async sendCareTypeSelectionEvent(): Promise<void> {
      await sendSegmentEventWithTimeout('Child Care Schedule Type', {
        care_type: this.draft.careType?.toLowerCase(),
      });
    },

    async sendJobPostedEvent(): Promise<void> {
      await sendSegmentEventWithTimeout('Job Posted - Client', {
        family_type: this.$store.getters['account/getAccount'].accountType(),
      });
    },

    setSubmitErrorMessage(): void {
      this.errors.submit = 'There was an error creating your job post. Please try again.';
    },

    determineRedirectPath(): string {
      if (this.$store.getters['account/getAccount'].isBasic()) {
        return '/upgrade/pricing?source=job-post';
      }

      if (this.redirectPath) {
        return this.redirectPath;
      }

      if (this.legacyShareJobRedirect) {
        return new URL(this.legacyShareJobRedirect, window.location.origin).href;
      }

      return '/account/seekers/dashboard';
    },

    async submit(): Promise<void> {
      this.isLoading = true;
      this.errors = {};

      try {
        const response = await this.$store.dispatch('job/createJobPost', this.draft);

        if (response.errors?.length > 0) {
          // mutation error
          console.error('Job post creation failed:', response.errors);
          this.setSubmitErrorMessage();
          this.isLoading = false;
          return;
        }

        await this.sendAnalyticsEvents();
        redirect(this.determineRedirectPath());
      } catch (error) {
        // network error
        this.setSubmitErrorMessage();
        console.error('Error creating job post:', error);
        this.isLoading = false;
      }
    },
  },
};
</script>
